import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui';
import { useCampaignStore } from '@/stores';
import { Coachmark } from './Coachmark';
import { TaskDemoOverlay } from './TaskDemoOverlay';

type Step = 'idle' | 'welcome' | 'explore' | 'task' | 'done';

// The single message that points a new manager at the left rail to explore on their own — chosen
// over a multi-step spotlight of each icon to keep the first run light (discover, don't lecture).
const EXPLORE_MESSAGE =
  'Your tools live here. Explore the icons on the left to find Field Ops, Analytics, your team, and more — open any to dive in.';

/**
 * First-run welcome for a manager landing in their workspace. A sleek welcome card, a single
 * pointer at the left rail to explore on their own, then a hand-off into the add-a-task demo on the
 * home calendar. Shown once per campaign. Deliberately light — one orientation beat, not a tour.
 */
export function WorkspaceWelcomeTour() {
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const seenKey = `sv:workspace-welcome-seen:${campaignId ?? 'none'}`;

  const [step, setStep] = useState<Step>('idle');
  const startedRef = useRef(false);

  const markSeen = () => {
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* storage disabled — it may just show again, which is fine */
    }
  };

  // Start once per campaign, the first time they reach the workspace.
  useEffect(() => {
    if (startedRef.current || !campaignId) return;
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) === '1';
    } catch {
      /* ignore */
    }
    if (seen) {
      startedRef.current = true;
      return;
    }
    startedRef.current = true;
    markSeen();
    setStep('welcome');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, seenKey]);

  if (step === 'idle' || step === 'done') return null;

  if (step === 'welcome') {
    // shadcn Dialog (Radix) gives the first-run welcome a real dialog role, aria-modal, and focus
    // trap. Dismissing (Esc / overlay / close button) ends the tour, same as the Skip button.
    return (
      <Dialog open onOpenChange={(open) => { if (!open) setStep('done'); }}>
        <DialogContent className="w-[400px] text-center sm:max-w-[400px]">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <DialogTitle className="text-center text-2xl">Welcome to your workspace</DialogTitle>
            <DialogDescription className="text-center">
              Let&rsquo;s get you oriented &mdash; then we&rsquo;ll start your first task.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button variant="ghost" onClick={() => setStep('done')}>
              Skip
            </Button>
            <Button onClick={() => setStep('explore')}>Show me around</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'explore') {
    return (
      <Coachmark
        active
        targetSelector='[data-coachmark="sidebar-nav"]'
        message={EXPLORE_MESSAGE}
        primaryLabel="Start your first task"
        placement="right"
        onDismiss={() => setStep('task')}
      />
    );
  }

  if (step === 'task') {
    return (
      <TaskDemoOverlay
        onSkip={() => setStep('done')}
        onGotIt={() => setStep('done')}
      />
    );
  }

  return null;
}
